export type Lang = "en" | "pt";

const en = {
  nav: { mission: "Mission", features: "Features", audience: "Who is it for", vision: "Vision", contact: "Contact" },
  hero: {
    eyebrow: "People Linked Around Your Sport",
    title1: "Sports for ",
    title2: "all",
    subtitle:
      "PLAYS democratizes access to sport, bringing athletes, clubs, organizations, and fans closer through one simple, modern, inclusive platform.",
    boldWord: "democratizes",
    cta: "Get in touch",
    badge: "Together",
    devNote: "App in development",
    imageAlt: "A community playing sport together",
  },
  sports: ["Football", "Basketball", "Tennis", "Athletics", "Climbing", "Cycling", "Hockey", "Padel", "Surf", "Volleyball", "Rugby", "Swimming"],
  mission: {
    eyebrow: "Our Mission",
    title: "We believe sport should be accessible to everyone.",
    cards: [
      { title: "Eliminate barriers", text: "Open doors to participation in any sport, at any level and anywhere." },
      { title: "Create opportunities", text: "Empower clubs, athletes and organizers with simple and modern tools." },
      { title: "Sustainable ecosystem", text: "Connect the sports world with technology that lasts." },
    ],
  },
  features: {
    eyebrow: "Built for the future of sport",
    title: "One platform. Every connection.",
    items: [
      { index: "01 / Feed", title: "Community Feed", text: "A timeline with everything you love about the sports you care about most." },
      { index: "02 / Profiles", title: "Athlete & Club Profiles", text: "Customizable profiles that match the expectations of everyone involved." },
      {
        index: "03 / Messaging",
        title: "Direct Messaging",
        text: "Talk to scouts, teammates, organizers and fans in a single inbox built for the rhythm of sport.",
      },
    ],
  },
  audience: {
    eyebrow: "Who is PLAYS for",
    title: "Every player in the game.",
    items: [
      { title: "Athletes", text: "Showcase your journey, connect with clubs, grow your network." },
      { title: "Clubs", text: "Recruit talent and communicate with the community." },
      { title: "Organizations", text: "Manage your sports network." },
      { title: "Fans", text: "Follow what you're passionate about and join the community you love." },
      { title: "Organizers", text: "Plan, promote and operate events end-to-end." },
    ],
  },
  vision: {
    badge: "The future of sport",
    title: "Technology is the new home field.",
    paragraph:
      "The future of sport lies in technology. PLAYS is part of that transformation, building a game that is more open, more connected, and more human for everyone who lives and breathes sport.",
    keyIdeaLabel: "Key idea",
    keyIdeaValue: "Find your game. Find your people.",
    cards: [
      {
        eyebrow: "For professionals",
        title: "Build, share, monetize.",
        description:
          "Everything you need to grow your presence and create opportunities in sport.",
        chips: ["Promote your services", "Organize events", "Job opportunities"],
      },
      {
        eyebrow: "For fans",
        title: "Live, play, discover.",
        description:
          "Be a part of the game.",
        chips: ["Community", "Events near you", "Experiences"],
      },
    ],
  },
  contact: {
    eyebrow: "Get in touch",
    title: "Talk to the team.",
    paragraph: "Ideas, questions, possibilities — let's connect.",
    writeLabel: "Write to us at",
    copy: "Copy",
    copied: "Copied",
    responseLabel: "Response",
    responseValue: "Within 48 business hours",
  },
  footer: {
    followUs: "Follow us",
    navigate: "Navigate",
    contact: "Contact",
    languages: "Languages",
    langsValue: "Português · English",
    legal: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    cookies: "Cookie settings",
    rights: "PLAYS. All rights reserved.",
    trademark:
      "PLAYS, its logos and visual identity are protected intellectual property. Use, reproduction or distribution without express authorisation is prohibited.",
  },
  cookies: {
    title: "Cookies & privacy",
    text: "We use essential cookies and, only with your consent, analytics cookies (PostHog) to understand how the site is used. Nothing is tracked unless you accept.",
    accept: "Accept",
    reject: "Reject",
    more: "Privacy Policy",
  },
  legal: { back: "Back to home", updated: "Last updated", index: "Contents" },
  toasts: { emailCopiedTitle: "Email copied" },
};

const pt: typeof en = {
  nav: { mission: "Missão", features: "Funcionalidades", audience: "Para quem é", vision: "Visão", contact: "Contacto" },
  hero: {
    eyebrow: "Pessoas ligadas ao teu desporto",
    title1: "Desporto para ",
    title2: "todos",
    subtitle:
      "A PLAYS democratiza o acesso ao desporto, aproximando atletas, clubes, organizações e adeptos através de uma plataforma simples, moderna e inclusiva.",
    boldWord: "democratiza",
    cta: "Entrar em contacto",
    badge: "Juntos",
    devNote: "App em desenvolvimento",
    imageAlt: "Uma comunidade a praticar desporto em conjunto",
  },
  sports: ["Futebol", "Basquetebol", "Ténis", "Atletismo", "Escalada", "Ciclismo", "Hóquei", "Padel", "Surf", "Voleibol", "Râguebi", "Natação"],
  mission: {
    eyebrow: "A Nossa Missão",
    title: "Acreditamos que o desporto deve ser acessível a todos.",
    cards: [
      { title: "Eliminar barreiras", text: "Abrir as portas à participação em qualquer desporto, a qualquer nível e em qualquer lugar." },
      { title: "Criar oportunidades", text: "Capacitar clubes, atletas e organizadores com ferramentas simples e modernas." },
      { title: "Ecossistema sustentável", text: "Ligar o mundo do desporto com tecnologia que perdura." },
    ],
  },
  features: {
    eyebrow: "Construído para o futuro do desporto",
    title: "Uma plataforma. Todas as ligações.",
    items: [
      { index: "01 / Feed", title: "Feed da Comunidade", text: "Uma cronologia com tudo o que adoras sobre os desportos que mais te interessam." },
      { index: "02 / Perfis", title: "Perfis de Atletas e Clubes", text: "Perfis personalizáveis à altura das expectativas de todos os envolvidos." },
      {
        index: "03 / Mensagens",
        title: "Mensagens Diretas",
        text: "Fala com olheiros, colegas de equipa, organizadores e adeptos, numa só caixa de entrada feita para o ritmo do desporto.",
      },
    ],
  },
  audience: {
    eyebrow: "Para quem é a PLAYS",
    title: "Todos os intervenientes do jogo.",
    items: [
      { title: "Atletas", text: "Mostra o teu percurso, liga-te a clubes e amplia a tua rede." },
      { title: "Clubes", text: "Recruta talento e comunica com a comunidade." },
      { title: "Organizações", text: "Gere a tua rede desportiva." },
      { title: "Adeptos", text: "Segue o que te apaixona e participa na comunidade que adoras." },
      { title: "Organizadores", text: "Planeia, promove e gere eventos de ponta a ponta." },
    ],
  },
  vision: {
    badge: "O futuro do desporto",
    title: "A\u00a0tecnologia é o novo campo de jogo.",
    paragraph:
      "O futuro do desporto está na tecnologia. A PLAYS faz parte dessa transformação, construindo um jogo mais aberto, mais ligado e mais humano para todos os que vivem e respiram desporto.",
    keyIdeaLabel: "Ideia-chave",
    keyIdeaValue: "Encontra o teu jogo. Encontra as tuas pessoas.",
    cards: [
      {
        eyebrow: "Para profissionais",
        title: "Criar, partilhar, monetizar.",
        description:
          "Tudo o que precisas para fazer crescer a tua presença e criar oportunidades no desporto.",
        chips: ["Promover os teus serviços", "Organizar eventos", "Oportunidades de emprego"],
      },
      {
        eyebrow: "Para adeptos",
        title: "Vive, joga, descobre.",
        description:
          "Faz parte do jogo.",
        chips: ["Comunidade", "Eventos perto de ti", "Experiências"],
      },
    ],
  },
  contact: {
    eyebrow: "Entrar em contacto",
    title: "Fala com a equipa.",
    paragraph: "Ideias, questões, possibilidades — vamos conversar.",
    writeLabel: "Escreve-nos para",
    copy: "Copiar",
    copied: "Copiado",
    responseLabel: "Resposta",
    responseValue: "No prazo de 48 horas úteis",
  },
  footer: {
    followUs: "Segue-nos",
    navigate: "Navegar",
    contact: "Contacto",
    languages: "Idiomas",
    langsValue: "Português · English",
    legal: "Legal",
    privacy: "Política de Privacidade",
    terms: "Termos e Condições de Utilização",
    cookies: "Definições de cookies",
    rights: "PLAYS. Todos os direitos reservados.",
    trademark:
      "PLAYS, os respetivos logótipos e identidade visual são propriedade intelectual protegida. É proibida a utilização, reprodução ou distribuição sem autorização expressa.",
  },
  cookies: {
    title: "Cookies e privacidade",
    text: "Utilizamos cookies essenciais e, apenas com o seu consentimento, cookies analíticos (PostHog) para perceber como o site é utilizado. Sem aceitação não é feito qualquer rastreio.",
    accept: "Aceitar",
    reject: "Recusar",
    more: "Política de Privacidade",
  },
  legal: { back: "Voltar ao início", updated: "Última atualização", index: "Índice" },
  toasts: { emailCopiedTitle: "Email copiado" },
};

export type Translation = typeof en;
export const translations: Record<Lang, Translation> = { en, pt };
